/**
 * Content Tabs Custom Element
 *
 * Handles tab switching functionality for featured sections with multiple tabs.
 * Re-initializes slider components when tabs are switched to ensure proper
 * carousel functionality.
 *
 * @example
 * <content-tabs>
 *   <button class="content-tabs__button active" js-tab-button="1">Tab 1</button>
 *   <button class="content-tabs__button" js-tab-button="2">Tab 2</button>
 *   <div js-tab-content="1">Content 1</div>
 *   <div js-tab-content="2">Content 2</div>
 *   <slider-component>...</slider-component>
 * </content-tabs>
 */
class ContentTabs extends HTMLElement {
  /**
   * Initialize the component
   */
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.content-tabs__button');
    this.contents = this.querySelectorAll('[js-tab-content]');
    this.sliderComponent = this.querySelector('slider-component');
  }

  /**
   * Called when element is added to the DOM
   * Sets up event listeners for tab buttons
   */
  connectedCallback() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => this.handleTabClick(tab));
    });
  }

  /**
   * Handle tab button click event
   * Updates active states, shows/hides content, and re-initializes slider
   *
   * @param {HTMLElement} tab - The clicked tab button element
   */
  handleTabClick(tab) {
    const target = tab.getAttribute('js-tab-button');

    this.tabs.forEach(t => t.classList.remove('active'));
    tab.classList.add('active');

    this.contents.forEach(content => {
      const isTarget = content.getAttribute('js-tab-content') === target;
      content.style.display = isTarget ? 'flex' : 'none';
    });

    this.resetSlider();
  }

  /**
   * Re-initialize slider component after tab switch
   * Uses setTimeout to ensure display changes are applied before recalculating
   * slider dimensions
   */
  resetSlider() {
    if (!this.sliderComponent) return;

    setTimeout(() => {
      if (typeof this.sliderComponent.resetPages === 'function') {
        this.sliderComponent.resetPages();
      }
    }, 50);
  }
}

if (!customElements.get('content-tabs')) {
  customElements.define('content-tabs', ContentTabs);
}
